<template>
  <div class="chart-box" :style="{height: `${height}px`}">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import {clipCeiling} from '@/utils/day-nutrition';

const PLANNED_COLOUR = 'rgba(33, 150, 243, 0.8)';
const SWAP_COLOUR = 'rgba(76, 175, 80, 0.6)';

/** writes the true count on each bar that the clipped y axis cuts off */
const clippedBarLabels = {
  id: 'clippedBarLabels',
  afterDatasetsDraw(chart) {
    const ceiling = chart.options.scales.y.max;
    if (ceiling === undefined) return;
    const {ctx, chartArea} = chart;
    const meta = chart.getDatasetMeta(0);
    ctx.save();
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#222';
    chart.data.datasets[0].data.forEach((count, index) => {
      if (count <= ceiling) return;
      // written down the bar, so the labels of neighbouring bars cannot run into each other
      ctx.save();
      ctx.translate(meta.data[index].x, chartArea.top + 3);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(count.toLocaleString(), 0, 0);
      ctx.restore();
    });
    ctx.restore();
  },
};

/**
 * how many swap combinations meet each number of targets, with the planned days marked.
 * the y axis is cut when one bar dwarfs the rest, so the shape of the distribution stays readable;
 * cut bars carry their true count.
 */
export default {
  name: 'TargetsMetChart',
  props: {
    // [{met, count}] for met = 0..total
    distribution: {type: Array, required: true},
    // [{met, label}]: the planned days, shown in a second colour on their bar
    planned: {type: Array, default: () => []},
    height: {type: Number, default: 220},
  },
  data() {
    return {chart: null};
  },
  computed: {
    ceiling() {
      return clipCeiling(this.distribution);
    },
  },
  mounted() {
    this.createChart();
  },
  watch: {
    distribution: {handler() { this.updateChart(); }, deep: true},
    planned: {handler() { this.updateChart(); }, deep: true},
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    plannedAt(met) {
      return this.planned.filter(day => day.met === met).map(day => day.label);
    },
    colours() {
      return this.distribution.map(bin => (this.plannedAt(bin.met).length ? PLANNED_COLOUR : SWAP_COLOUR));
    },
    yScale() {
      const scale = {beginAtZero: true, title: {display: true, text: 'combinations'}, ticks: {precision: 0}};
      if (this.ceiling !== null) {
        scale.max = this.ceiling;
        scale.title.text = `combinations (axis cut at ${this.ceiling})`;
      }
      return scale;
    },
    createChart() {
      const ctx = this.$refs.canvas.getContext('2d');
      this.chart = new Chart(ctx, {
        type: 'bar',
        plugins: [clippedBarLabels],
        data: {
          labels: this.distribution.map(bin => String(bin.met)),
          datasets: [{
            label: 'Swap combinations',
            data: this.distribution.map(bin => bin.count),
            backgroundColor: this.colours(),
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {display: false},
            tooltip: {
              callbacks: {
                title: items => `${items[0].label} targets met`,
                label: item => {
                  const planned = this.plannedAt(Number(item.label));
                  return [`${item.raw} combinations`, ...(planned.length ? [`planned: ${planned.join(', ')}`] : [])];
                },
              },
            },
          },
          scales: {
            x: {title: {display: true, text: 'targets met'}},
            y: this.yScale(),
          },
        },
      });
    },
    updateChart() {
      if (!this.chart) return;
      this.chart.data.labels = this.distribution.map(bin => String(bin.met));
      this.chart.data.datasets[0].data = this.distribution.map(bin => bin.count);
      this.chart.data.datasets[0].backgroundColor = this.colours();
      this.chart.options.scales.y = this.yScale();
      this.chart.update();
    },
  },
};
</script>

<style scoped>
/* chart.js needs a box with its own height, or the responsive canvas grows the column that grows the canvas */
.chart-box {
  position: relative;
  width: 100%;
}
</style>
